<template>
  <text-box :label="titleString" :icon="icon" :hideTitle="hideTitle">
    <template v-slot:info>
      <percent-chip v-if="precision" :value="precision" :best="100" icon="mdi-bullseye-arrow"/>
      <slot name="info"></slot>
    </template>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <!-- Changed from svg to canvas, because of a bug in echarts:
              https://github.com/apache/incubator-echarts/issues/9565
            -->
          <v-chart :option="options" :init-options="{renderer: 'canvas'}" autoresize :style="`height: ${chartHeight}; width: 100%;`"/>
        </v-col>
      </v-row>
    </v-container>
  </text-box>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/line'
import 'echarts/lib/chart/bar'
import 'echarts/lib/component/markLine'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/toolbox'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/legendScroll'
import 'echarts/lib/component/dataZoom'
import { graphic } from 'echarts/core'
// import 'zrender/lib/svg/svg'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'
import moment from 'moment'
import TextBox from '@/components/TextBox.vue'
import PercentChip from '@/components/PercentChip.vue'

export default {
  data () {
    return {}
  },
  computed: {
    chartHeight () {
      if (!this.showDataZoom) {
        return '320px'
      }
      return this.period === 'week' ? '370px' : '345px'
    },
    zoomGridBottom () {
      if (!this.showDataZoom) {
        return 0
      }
      return this.period === 'week' ? 95 : 68
    },
    options () {
      return {
        animation: this.animation,
        tooltip: {
          trigger: 'axis',
          formatter: this.formatAreaTooltip
        },
        toolbox: {
          show: this.export,
          left: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.RiverChart.dataView'),
              lang: [this.$t('components.RiverChart.dataView'), this.$t('components.RiverChart.close'), this.$t('components.RiverChart.refresh')]
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || this.titleString || 'ExportedImage',
              title: this.$t('components.RiverChart.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        legend: {
          show: !this.hideLegend,
          data: this.channelKeys,
          type: 'scroll',
          orient: 'vertical',
          right: 0,
          top: 0,
          selector: [
            {
              type: 'all',
              title: this.$t('components.RiverChart.selectAll')
            },
            {
              type: 'inverse',
              title: this.$t('components.RiverChart.invert')
            }
          ]
        },
        grid: {
          top: 10,
          left: 50,
          right: !this.hideLegend ? Math.max(this.$i18n.locale === 'es' || this.$i18n.locale === 'pt' ? 180 : 150, this.longestLabelLength * 10) : 100,
          bottom: this.zoomGridBottom,
          containLabel: true
        },
        xAxis: {
          type: this.xAxisTypeIsCategory ? 'category' : 'value',
          name: this.xName,
          nameLocation: 'center',
          nameGap: 30,
          min: this.xAxisTypeIsCategory ? null : 0,
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            formatter: this.xAxisTypeIsCategory ? (this.xAxisTypeIsDate ? this.formatDate : null) : this.formatValue,
            rotate: this.xAxisRotate ? this.xAxisRotate : null
          },
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          name: this.yName,
          nameLocation: 'middle',
          nameGap: 50,
          min: this.showNegatives ? null : 0,
          max: this.sumOneHundred ? 100 : null,
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLine: {
            show: true
          },
          axisTick: {
            show: true
          },
          axisLabel: {
            formatter: this.formatValue
          }
        },
        dataZoom: this.showDataZoom ? [
          {
            type: 'slider',
            show: true,
            xAxisIndex: [0],
            start: 0,
            end: 100,
            bottom: 10
          }
        ] : [],
        series: this.series
      }
    },
    series: function () {
      let series = this.channelKeys.map(channel => {
        const hasColor = this.colors && channel in this.colors
        const s = {
          name: channel,
          type: 'line',
          lineStyle: {
            width: this.stacked ? 0.5 : 2
          },
          data: this.dataArray(this.data[channel]),
          showSymbol: false,
          smooth: 0.25,
          smoothMonotone: 'x'
        }
        if (this.stacked) {
          s.stack = 'media'
          s.areaStyle = {}
          if (hasColor) {
            s.itemStyle = {
              color: new graphic.LinearGradient(0, 3, 0, 0, [
                { offset: 0, color: 'white' },
                { offset: 1, color: this.colors[channel] }
              ])
            }
          }
        } else if (hasColor) {
          s.itemStyle = { color: this.colors[channel] }
        }
        return s
      })
      return series
    },
    channelKeys: function () {
      return Object.keys(this.data)
    },
    longestLabelLength: function () {
      var longest = this.channelKeys.reduce(
        function (a, b) {
          return a.length > b.length ? a : b
        }
      )
      return longest.length
    }
  },
  methods: {
    formatAreaTooltip: function (params) {
      var tooltip = `<b style="font-size: 16px;">${this.xAxisTypeIsDate ? this.formatDate(params[0].value[0]) : params[0].value[0]}</b><br><br>`
      for (let index = params.length - 1; index >= 0; index--) {
        const param = params[index]
        const color = (param.color && param.color.colorStops && param.color.colorStops.length > 1 && param.color.colorStops[1] && param.color.colorStops[1].color)
          ? param.color.colorStops[1].color
          : (param.color || '#999999')
        tooltip += `
          <b style="color: ${color}">▶</b>
          ${param.seriesName}:
          <span style="color: lightblue; font-size: 16px;">${this.formatValue(param.value[1])}${this.sumOneHundred ? '%' : ''}</span>
          <br>
        `
      }
      return tooltip
    },
    dataArray: function (data) {
      var array = []
      for (var i in data.x) {
        array.push([
          data.x[i],
          data.y[i]
        ])
      }
      return array
    },
    formatDate (date) {
      if (this.period === 'month') {
        return moment(date).format('MMM \'YY')
      } else {
        return moment(date).format('\'YY-MMM-DD')
      }
    },
    formatValue: function (value) {
      if (value && !isNaN(value)) {
        return numbro(value).format({
          average: true,
          lowPrecision: false,
          mantissa: 1,
          optionalMantissa: true
        })
      } else {
        return '–'
      }
    }
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: false
    },
    titleString: {
      type: String,
      required: false,
      default: function () {
        return ''
      }
    },
    exportImageName: {
      type: String,
      required: false
    },
    precision: {
      type: Number,
      required: false
    },
    icon: {
      type: String,
      required: false,
      default: function () {
        return ''
      }
    },
    xName: {
      type: String,
      required: false
    },
    yName: {
      type: String,
      required: false
    },
    xAxisTypeIsCategory: {
      type: Boolean,
      required: false
    },
    xAxisTypeIsDate: {
      type: Boolean,
      required: false
    },
    xAxisRotate: {
      type: Number,
      required: false
    },
    gridBottomMargin: {
      type: Number,
      required: false
    },
    sumOneHundred: {
      type: Boolean,
      required: false
    },
    smooth: {
      type: Boolean,
      required: false
    },
    stacked: {
      type: Boolean,
      required: false,
      default: function () {
        return true
      }
    },
    showDataZoom: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    period: {
      type: String,
      required: false,
      default: function () {
        return 'month'
      }
    },
    hideTitle: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    hideLegend: {
      type: Boolean,
      required: false,
      default () {
        return false
      }
    },
    animation: {
      type: Boolean,
      required: false,
      default () {
        return true
      }
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox,
    PercentChip
  }
}
</script>
